import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

// Mock user data
const mockUser = {
  name: 'Alex',
  avatar: null,
  totalBalance: 12458.32,
  totalBalanceChange: '+2.34%',
  isBalancePositive: true
};

// Mock portfolio assets
const mockPortfolio = [
  {
    id: 'btc',
    name: 'Bitcoin',
    symbol: 'BTC',
    icon: '₿',
    balance: 0.1842,
    value: 18478.15,
    change: '+3.42%',
    changeType: 'positive',
    color: '#f7931a'
  },
  {
    id: 'eth',
    name: 'Ethereum',
    symbol: 'ETH',
    icon: 'Ξ',
    balance: 2.5,
    value: 8202.20,
    change: '+1.85%',
    changeType: 'positive',
    color: '#627eea'
  },
  {
    id: 'usdt',
    name: 'Tether',
    symbol: 'USDT',
    icon: '₮',
    balance: 2500.00,
    value: 2500.00,
    change: '0.00%',
    changeType: 'neutral',
    color: '#26a17b'
  },
  {
    id: 'sol',
    name: 'Solana',
    symbol: 'SOL',
    icon: '◎',
    balance: 15.8,
    value: 2418.14,
    change: '-2.15%',
    changeType: 'negative',
    color: '#14f195'
  }
];

// Mock recent activity
const mockActivity = [
  {
    id: 1,
    type: 'receive',
    title: 'Received BTC',
    description: 'From external wallet',
    amount: '+0.0234 BTC',
    value: '+$2,347.82',
    time: '2 hours ago',
    icon: 'receive'
  },
  {
    id: 2,
    type: 'swap',
    title: 'Swapped ETH → USDT',
    description: 'Swap completed',
    amount: '0.5 ETH',
    value: '$1,640.44',
    time: '5 hours ago',
    icon: 'swap'
  },
  {
    id: 3,
    type: 'p2p',
    title: 'P2P Trade Completed',
    description: 'Sold USDT to @trader_mike',
    amount: '-500 USDT',
    value: '$500.00',
    time: 'Yesterday',
    icon: 'p2p'
  },
  {
    id: 4,
    type: 'send',
    title: 'Sent SOL',
    description: 'To external wallet',
    amount: '-5.2 SOL',
    value: '-$796.36',
    time: '2 days ago',
    icon: 'send'
  }
];

const mockMarkets = [
  {
    id: 'btc',
    name: 'BTC/USDT',
    icon: '₿',
    price: '$100,299.58',
    change: '+3.42%',
    changeType: 'positive',
    color: '#f7931a'
  },
  {
    id: 'eth',
    name: 'ETH/USDT',
    icon: 'Ξ',
    price: '$3,280.88',
    change: '+1.85%',
    changeType: 'positive',
    color: '#627eea'
  },
  {
    id: 'sol',
    name: 'SOL/USDT',
    icon: '◎',
    price: '$153.05',
    change: '-2.15%',
    changeType: 'negative',
    color: '#14f195'
  },
  {
    id: 'xrp',
    name: 'XRP/USDT',
    icon: '✕',
    price: '$2.16',
    change: '+5.23%',
    changeType: 'positive',
    color: '#23292f'
  },
  {
    id: 'bnb',
    name: 'BNB/USDT',
    icon: '◆',
    price: '$645.32',
    change: '+0.87%',
    changeType: 'positive',
    color: '#f3ba2f'
  }
];

const mockFaqs = [
  {
    id: 1,
    question: 'How do I buy cryptocurrency on Coinsher?',
    answer: 'To start buying cryptocurrency on Coinsher, simply sign up using your email or phone, verify your account swiftly, and then you\'re all set to deposit funds using a variety of channels including your crypto wallet and fiat.'
  },
  {
    id: 2,
    question: 'Can I use a credit card to purchase cryptocurrency?',
    answer: 'Yes, Coinsher supports multiple payment methods including credit cards for purchasing cryptocurrency.'
  },
  {
    id: 3,
    question: 'Why should I choose Coinsher for my crypto trading',
    answer: 'Coinsher offers competitive rates, high security, and a user-friendly interface for all your crypto trading needs.'
  },
  {
    id: 4,
    question: 'How does Coinsher ensure the security of my investments?',
    answer: 'We use industry-standard security measures including encryption, two-factor authentication, and cold storage for your assets.'
  },
  {
    id: 5,
    question: 'What range of cryptocurrencies does Coinsher Exchange offer?',
    answer: 'Coinsher Exchange offers a wide range of cryptocurrencies including Bitcoin, Ethereum, and many altcoins.'
  }
];

// Withdraw Method Modal Component
interface WithdrawMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCrypto: () => void;
  onSelectEmail: () => void;
}

const WithdrawMethodModal: React.FC<WithdrawMethodModalProps> = ({
  isOpen,
  onClose,
  onSelectCrypto,
  onSelectEmail
}) => {
  if (!isOpen) return null;

  return (
    <div className="withdraw-modal-overlay" onClick={onClose}>
      <div className="withdraw-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Drag handle */}
        <div className="withdraw-modal-handle"></div>

        {/* Close button */}
        <button className="withdraw-modal-close" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Title */}
        <h2 className="withdraw-modal-title">Choose Withdrawal Method</h2>

        {/* Option 1: Send via Crypto Network */}
        <button className="withdraw-modal-option withdraw-modal-option-selected" onClick={onSelectCrypto}>
          <div className="withdraw-modal-option-icon withdraw-modal-option-icon-crypto">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <path d="M7 21L21 7M21 7H9M21 7V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="withdraw-modal-option-text">
            <h3 className="withdraw-modal-option-title">Send via Crypto Network</h3>
            <p className="withdraw-modal-option-desc">Send to any crypto wallet address on blockchain network</p>
          </div>
        </button>

        {/* Option 2: Send via Email */}
        <button className="withdraw-modal-option" onClick={onSelectEmail}>
          <div className="withdraw-modal-option-icon withdraw-modal-option-icon-email">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <rect x="3" y="6" width="22" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
              <path d="M3 8L14 15L25 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="withdraw-modal-option-text">
            <h3 className="withdraw-modal-option-title">Send via Email</h3>
            <p className="withdraw-modal-option-desc">Instant transfer to any user. Easy and secure.</p>
          </div>
        </button>
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'markets' | 'gainers' | 'losers'>('markets');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [activeNav, setActiveNav] = useState<string>('home');
  const [balanceVisible, setBalanceVisible] = useState(true);
  const [tradeModalOpen, setTradeModalOpen] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);

  const toggleFaq = (id: number) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'receive':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 5V19M12 19L5 12M12 19L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'send':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 19V5M12 5L5 12M12 5L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'swap':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M7 16L3 12M3 12L7 8M3 12H21M17 8L21 12M21 12L17 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'p2p':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="7" cy="7" r="3" stroke="currentColor" strokeWidth="2"/>
            <circle cx="17" cy="17" r="3" stroke="currentColor" strokeWidth="2"/>
            <path d="M9 9L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="dashboard-user-greeting">
          <div className="user-avatar">
            {mockUser.avatar ? (
              <img src={mockUser.avatar} alt="User avatar" />
            ) : (
              <span>{mockUser.name.charAt(0)}</span>
            )}
          </div>
          <div className="greeting-text">
            <span className="greeting-hello">Good morning 👋</span>
            <span className="greeting-name">{mockUser.name}</span>
          </div>
        </div>
        <div className="dashboard-header-actions">
          <button
            className="dashboard-notification-button"
            aria-label="Notifications"
            onClick={() => navigate('/notifications')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 5.33333C12 4.27247 11.5786 3.25505 10.8284 2.50491C10.0783 1.75476 9.06087 1.33333 8 1.33333C6.93913 1.33333 5.92172 1.75476 5.17157 2.50491C4.42143 3.25505 4 4.27247 4 5.33333C4 10 2 11.3333 2 11.3333H14C14 11.3333 12 10 12 5.33333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9.15335 14C9.03614 14.2021 8.86791 14.3698 8.6655 14.4864C8.46309 14.6029 8.2336 14.6643 8.00001 14.6643C7.76643 14.6643 7.53694 14.6029 7.33453 14.4864C7.13212 14.3698 6.96389 14.2021 6.84668 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="notification-badge">3</span>
          </button>
          <button className="dashboard-grid-button" aria-label="Settings">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 1V4M12 20V23M4.22 4.22L6.34 6.34M17.66 17.66L19.78 19.78M1 12H4M20 12H23M4.22 19.78L6.34 17.66M17.66 6.34L19.78 4.22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="dashboard-balance-card">
          <div className="balance-header">
            <span className="balance-label">Total Portfolio Value</span>
            <button
              className="balance-toggle"
              aria-label="Toggle visibility"
              onClick={() => setBalanceVisible(!balanceVisible)}
            >
              {balanceVisible ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M14 8C14 8 11.5 12 8 12C4.5 12 2 8 2 8C2 8 4.5 4 8 4C11.5 4 14 8 14 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2 2L14 14M6.5 6.5C6.18 6.82 6 7.27 6 7.73C6 8.73 6.82 9.55 7.82 9.55C8.28 9.55 8.73 9.37 9.05 9.05M10.73 10.73C9.93 11.31 8.99 11.64 8 11.64C4.5 11.64 2 7.73 2 7.73C2 7.73 2.91 6.18 4.36 5.09M6.91 3.91C7.27 3.82 7.64 3.73 8 3.73C11.5 3.73 14 7.73 14 7.73C14 7.73 13.45 8.64 12.55 9.55" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </div>
          <div className="balance-amount-row">
            <h1 className="balance-amount">
              {balanceVisible ? formatCurrency(mockUser.totalBalance) : '••••••'}
            </h1>
            <span className={`balance-change ${mockUser.isBalancePositive ? 'positive' : 'negative'}`}>
              {mockUser.totalBalanceChange}
            </span>
          </div>
          <p className="balance-subtitle">Updated just now</p>

          <div className="dashboard-actions">
            <button className="action-button" onClick={() => navigate('/deposit')}>
              <div className="action-icon action-icon-deposit">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 19V5M12 5L5 12M12 5L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="action-label">Deposit</span>
            </button>
            <button className="action-button" onClick={() => setIsWithdrawModalOpen(true)}>
              <div className="action-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 5V19M10 19L3 12M10 19L17 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="action-label">Withdraw</span>
            </button>
            <button className="action-button">
              <div className="action-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 14L3 10M3 10L7 6M3 10H17M13 6L17 10M17 10L13 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="action-label">Swap</span>
            </button>
            <button className="action-button" onClick={() => navigate('/p2p-buy-list')}>
              <div className="action-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="6" cy="6" r="3" stroke="currentColor" strokeWidth="1.5"/>
                  <circle cx="14" cy="14" r="3" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M8.5 7.5L11.5 12.5M11.5 7.5L8.5 12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <span className="action-label">P2P</span>
            </button>
          </div>
        </div>

        {/* Portfolio Section */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">My Portfolio</h2>
            <button className="view-all-button">View All →</button>
          </div>

          <div className="portfolio-list">
            {mockPortfolio.map((asset) => (
              <button key={asset.id} className="portfolio-item">
                <div className="portfolio-icon" style={{ backgroundColor: asset.color }}>
                  {asset.icon}
                </div>
                <div className="portfolio-info">
                  <div className="portfolio-name">{asset.name}</div>
                  <div className="portfolio-balance">
                    {balanceVisible ? `${asset.balance} ${asset.symbol}` : '••••'}
                  </div>
                </div>
                <div className="portfolio-value-col">
                  <div className="portfolio-value">
                    {balanceVisible ? formatCurrency(asset.value) : '••••'}
                  </div>
                  <div className={`portfolio-change ${asset.changeType}`}>
                    {asset.change}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Recent Activity Section */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Recent Activity</h2>
            <button className="view-all-button">See All →</button>
          </div>

          <div className="activity-list">
            {mockActivity.map((activity) => (
              <div key={activity.id} className="activity-item">
                <div className={`activity-icon ${activity.type}`}>
                  {getActivityIcon(activity.type)}
                </div>
                <div className="activity-info">
                  <div className="activity-title">{activity.title}</div>
                  <div className="activity-description">{activity.description}</div>
                </div>
                <div className="activity-amount-col">
                  <div className={`activity-amount ${activity.type === 'receive' ? 'positive' : ''}`}>
                    {activity.amount}
                  </div>
                  <div className="activity-time">{activity.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Live Markets Section */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Live Markets</h2>
            <button className="view-all-button">View All →</button>
          </div>

          <div className="market-tabs">
            <button 
              className={`market-tab ${activeTab === 'markets' ? 'active' : ''}`}
              onClick={() => setActiveTab('markets')}
            >
              Top Markets
            </button>
            <button 
              className={`market-tab ${activeTab === 'gainers' ? 'active' : ''}`}
              onClick={() => setActiveTab('gainers')}
            >
              Top Gainers
            </button>
            <button 
              className={`market-tab ${activeTab === 'losers' ? 'active' : ''}`}
              onClick={() => setActiveTab('losers')}
            >
              Top Losers
            </button>
          </div>

          <div className="market-list">
            {mockMarkets.map((market) => (
              <button key={market.id} className="market-item">
                <div className="market-icons">
                  <div className="coin-icon coin-icon-primary" style={{ backgroundColor: market.color }}>
                    {market.icon}
                  </div>
                  <div className="coin-icon coin-icon-usdt">₮</div>
                </div>
                <div className="market-info">
                  <div className="market-name">{market.name}</div>
                  <div className={`market-change ${market.changeType}`}>
                    {market.change} 24h
                  </div>
                </div>
                <div className="market-price">{market.price}</div>
                <svg className="market-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            ))}
          </div>
        </div>

        {/* Quick Actions Section */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Quick Actions</h2>
          </div>
          
          <div className="quick-actions-grid">
            <button className="quick-action-card" onClick={() => navigate('/p2p-buy-list')}>
              <div className="quick-action-icon buy">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.707 15.293C4.077 15.923 4.523 17 5.414 17H17M17 17C15.895 17 15 17.895 15 19C15 20.105 15.895 21 17 21C18.105 21 19 20.105 19 19C19 17.895 18.105 17 17 17ZM9 19C9 20.105 8.105 21 7 21C5.895 21 5 20.105 5 19C5 17.895 5.895 17 7 17C8.105 17 9 17.895 9 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="quick-action-label">Buy Crypto</span>
            </button>
            <button className="quick-action-card" onClick={() => navigate('/p2p-sell-list')}>
              <div className="quick-action-icon sell">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2V22M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="quick-action-label">Sell Crypto</span>
            </button>
            <button className="quick-action-card">
              <div className="quick-action-icon convert">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M7 16L3 12M3 12L7 8M3 12H21M17 8L21 12M21 12L17 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="quick-action-label">Convert</span>
            </button>
            <button className="quick-action-card" onClick={() => navigate('/p2p-merchant-dashboard')}>
              <div className="quick-action-icon merchant">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="quick-action-label">Merchant</span>
            </button>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Help & FAQ</h2>
            <button className="view-all-button">View All →</button>
          </div>

          <div className="faq-list">
            {mockFaqs.map((faq) => (
              <div key={faq.id} className={`faq-item ${expandedFaq === faq.id ? 'expanded' : ''}`}>
                <button 
                  className="faq-question"
                  onClick={() => toggleFaq(faq.id)}
                >
                  <div className="faq-number">Q{faq.id}</div>
                  <h3 className="faq-question-text">{faq.question}</h3>
                  <div className="faq-toggle-icon">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </button>
                {expandedFaq === faq.id && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <nav className="dashboard-nav">
        <button
          className={`nav-item ${activeNav === 'home' ? 'active' : ''}`}
          onClick={() => setActiveNav('home')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Home</span>
        </button>
        <button
          className={`nav-item ${activeNav === 'markets' ? 'active' : ''}`}
          onClick={() => navigate('/markets')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22 12L18 16L14 12L10 16L6 12L2 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 6L18 10L14 6L10 10L6 6L2 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Markets</span>
        </button>
        <button
          className={`nav-item ${activeNav === 'trade' ? 'active' : ''}`}
          onClick={() => {
            setActiveNav('trade');
            setTradeModalOpen(true);
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2V22M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Trade</span>
        </button>
        <button
          className={`nav-item ${activeNav === 'history' ? 'active' : ''}`}
          onClick={() => setActiveNav('history')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 21L12 16L5 21V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H17C17.5304 3 18.0391 3.21071 18.4142 3.58579C18.7893 3.96086 19 4.46957 19 5V21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>History</span>
        </button>
        <button
          className={`nav-item ${activeNav === 'assets' ? 'active' : ''}`}
          onClick={() => navigate('/assets')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 16V8C20.9996 7.64927 20.9071 7.30481 20.7315 7.00116C20.556 6.69751 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.69751 3.26846 7.00116C3.09294 7.30481 3.00036 7.64927 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.9979 12 21.9979C12.3511 21.9979 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Assets</span>
        </button>
      </nav>

      {/* Trade Modal */}
      {tradeModalOpen && (
        <div className="trade-modal-overlay" onClick={() => setTradeModalOpen(false)}>
          <div className="trade-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <button
              className="trade-modal-close"
              onClick={() => setTradeModalOpen(false)}
              aria-label="Close modal"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <button className="trade-modal-option" onClick={() => navigate('/p2p-buy-list')}>
              <div className="trade-modal-option-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="7" cy="7" r="3" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="17" cy="17" r="3" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9 9L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="trade-modal-option-content">
                <h3 className="trade-modal-option-title">P2P</h3>
                <p className="trade-modal-option-description">
                  Unlock financial freedom through P2P crypto trading: connect directly, trade efficiently, and decentralize your assets.
                </p>
              </div>
              <svg className="trade-modal-option-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <button className="trade-modal-option">
              <div className="trade-modal-option-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.707 15.293C4.077 15.923 4.523 17 5.414 17H17M17 17C15.895 17 15 17.895 15 19C15 20.105 15.895 21 17 21C18.105 21 19 20.105 19 19C19 17.895 18.105 17 17 17ZM9 19C9 20.105 8.105 21 7 21C5.895 21 5 20.105 5 19C5 17.895 5.895 17 7 17C8.105 17 9 17.895 9 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="trade-modal-option-content">
                <h3 className="trade-modal-option-title">Buy Crypto</h3>
                <p className="trade-modal-option-description">
                  Maximize crypto gains: Buy low, Sell high. Navigate digital markets with precision for optimal profits.
                </p>
              </div>
              <svg className="trade-modal-option-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Withdraw Method Modal */}
      <WithdrawMethodModal
        isOpen={isWithdrawModalOpen}
        onClose={() => setIsWithdrawModalOpen(false)}
        onSelectCrypto={() => {
          setIsWithdrawModalOpen(false);
          // Navigation to be added later
        }}
        onSelectEmail={() => {
          setIsWithdrawModalOpen(false);
          navigate('/withdrawal-email-transfer');
        }}
      />
    </div>
  );
};

export default Dashboard;